import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../styles/step1WritingModal.module.css';
import {
  Step1PresentationPlan,
  Step1WritingInventoryPayload,
  Step1WritingMetadata,
  Step1WritingMode,
  STEP1_WRITING_TEMPLATE,
  isStep1PathSelected,
} from '../types/step1Writing';
import {
  buildMetadataFromState,
  clearStep1WritingAll,
  downloadOriginalFile,
  listStep1ArchiveSummaries,
  loadStep1WritingPayload,
  payloadHasMeaningfulStep1Content,
  readStep1LocalBackup,
  replaceStep1WritingPayload,
  restoreStep1FromArchive,
  saveStep1WritingPayload,
  Step1ArchiveSummary,
  Step1SaveKind,
} from '../services/step1WritingService';
import { formatStoryLengthLabel, estimateReadTime, formatAloudDurationLabel } from '../utils/readTimeEstimate';
import { STEP1_CHAPTERS } from '../config/step1CoverageTopics';
import {
  STEP1_LIFE_STORY_PROMPTS,
  globalPromptIndex,
  promptsForChapter,
} from '../config/step1LifeStoryPrompts';
import { blocksForFlowWeave, fingerprintWizardAnswers, localFlowWeave, localDraftChecklist } from '../utils/step1DraftGenerator';
import { extractStep1UploadFile } from '../utils/step1FileExtract';
import StepShareDialog from './StepShareDialog';
import Step1RecoverOverlay from './Step1RecoverOverlay';
import { generateFirstStepShareHtml } from '../services/firstStepShareHtml';
import { downloadFirstStepStory, FirstStepDownloadFormat } from '../utils/firstStepDownload';
import { createDefaultStep1WritingMetadata } from '../utils/step1WritingMigration';
import { STEP1_ENCRYPTION_REMINDER, STEP1_METHOD_STEPS, STEP1_PATH_OPTIONS, methodTitle } from '../config/step1WritingMethodGuide';
import { useDialog } from '../context/DialogContext';

interface Step1WritingModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: { name?: string; display_name?: string; email?: string };
  metadata: Step1WritingMetadata;
  onSaveMetadata: (metadata: Step1WritingMetadata) => Promise<void>;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Above this word count, story textareas use a compact scrollable panel by default */
const LONG_DRAFT_WORD_THRESHOLD = 400;

const RESTORE_SUCCESS_MESSAGE =
  'Restored an earlier save. Your previous text was kept as another save.';

const Step1WritingModal: React.FC<Step1WritingModalProps> = ({
  isOpen,
  onClose,
  user,
  metadata,
  onSaveMetadata,
}) => {
  const { showConfirm, showAlert } = useDialog();
  const [payload, setPayload] = useState<Step1WritingInventoryPayload | null>(null);
  const [localMeta, setLocalMeta] = useState<Step1WritingMetadata>(metadata);
  const [showPathPicker, setShowPathPicker] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [wizardPromptIndex, setWizardPromptIndex] = useState(0);
  const [isAssembling, setIsAssembling] = useState(false);
  const [isPayloadLoading, setIsPayloadLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const draftAnswersBaselineRef = useRef<string | null>(null);
  const payloadLoadSucceededRef = useRef(false);
  const [isStoryShareOpen, setIsStoryShareOpen] = useState(false);
  const [isDraftEditorOpen, setIsDraftEditorOpen] = useState(false);
  const [draftEditBuffer, setDraftEditBuffer] = useState('');
  const [draftEditBaseline, setDraftEditBaseline] = useState('');
  const [draftEditorConfig, setDraftEditorConfig] = useState<{
    title: string;
    ariaLabel: string;
    placeholder?: string;
  } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const draftEditorRef = useRef<HTMLDivElement | null>(null);
  const presentationSectionRef = useRef<HTMLDivElement | null>(null);
  const reviewSectionRef = useRef<HTMLDivElement | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [isStoryViewOpen, setIsStoryViewOpen] = useState(false);
  const [isRecoverOpen, setIsRecoverOpen] = useState(false);
  const [archiveSummaries, setArchiveSummaries] = useState<Step1ArchiveSummary[]>([]);
  const [isArchivesLoading, setIsArchivesLoading] = useState(false);
  const [showMissingStoryOffer, setShowMissingStoryOffer] = useState(false);
  const [isDownloadPickerOpen, setIsDownloadPickerOpen] = useState(false);
  const [isClearAllOpen, setIsClearAllOpen] = useState(false);
  const [clearAllAcknowledged, setClearAllAcknowledged] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const payloadRef = useRef<Step1WritingInventoryPayload | null>(null);
  const metaRef = useRef(localMeta);

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);
  useEffect(() => {
    metaRef.current = localMeta;
  }, [localMeta]);

  const userBackupKey = user?.email || user?.name || 'anonymous';

  const openRecoverPanel = useCallback(async () => {
    setIsRecoverOpen(true);
    setIsArchivesLoading(true);
    try {
      const summaries = await listStep1ArchiveSummaries();
      setArchiveSummaries(summaries);
    } catch {
      setArchiveSummaries([]);
    } finally {
      setIsArchivesLoading(false);
    }
  }, []);

  const applyPayloadFromServer = useCallback((p: Step1WritingInventoryPayload) => {
    setPayload(p);
    payloadLoadSucceededRef.current = true;
    draftAnswersBaselineRef.current =
      p.assembledAnswersFingerprint ?? fingerprintWizardAnswers(p.wizardAnswers || {});
    const idx = p.wizardStepIndex ?? 0;
    setWizardPromptIndex(Math.min(idx, STEP1_LIFE_STORY_PROMPTS.length - 1));
  }, []);

  const restoreArchive = useCallback(
    (summary: Step1ArchiveSummary) => {
      showConfirm({
        title: 'Use this earlier save?',
        message: `Restore your story from ${summary.label}? Your current text is kept as another save.`,
        confirmText: 'Use this save',
        cancelText: 'Cancel',
        variant: 'warning',
        onConfirm: async () => {
          try {
            const restored = await restoreStep1FromArchive(summary.version, userBackupKey);
            applyPayloadFromServer(restored);
            const built = buildMetadataFromState(localMeta, restored);
            await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
            setLocalMeta(built);
            setIsRecoverOpen(false);
            setShowMissingStoryOffer(false);
            showAlert({ message: RESTORE_SUCCESS_MESSAGE, variant: 'success' });
          } catch {
            showAlert({ message: 'Could not restore that save. Try again.', variant: 'warning' });
          }
        },
      });
    },
    [applyPayloadFromServer, localMeta, onSaveMetadata, showAlert, showConfirm, userBackupKey]
  );

  const restoreLocalBackup = useCallback(() => {
    const local = readStep1LocalBackup(userBackupKey);
    if (!local) {
      showAlert({ message: 'No save found on this phone or computer.', variant: 'info' });
      return;
    }
    const savedLabel = new Date(local.savedAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    showConfirm({
      title: 'Use this earlier save?',
      message: `Restore the copy saved on this phone or computer at ${savedLabel}? Your current text is kept as another save.`,
      confirmText: 'Use this save',
      cancelText: 'Cancel',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await replaceStep1WritingPayload(local.payload, {
            kind: 'restore',
            archiveReason: 'Before restoring device backup',
            localBackupUserKey: userBackupKey,
          });
          applyPayloadFromServer(local.payload);
          const built = buildMetadataFromState(localMeta, local.payload);
          await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
          setLocalMeta(built);
          setIsRecoverOpen(false);
          setShowMissingStoryOffer(false);
          showAlert({ message: RESTORE_SUCCESS_MESSAGE, variant: 'success' });
        } catch {
          showAlert({ message: 'Could not restore that save. Try again.', variant: 'warning' });
        }
      },
    });
  }, [applyPayloadFromServer, localMeta, onSaveMetadata, showAlert, showConfirm, userBackupKey]);

  const checkForMissingStoryOffer = useCallback(
    async (serverPayload: Step1WritingInventoryPayload, writingMeta: Step1WritingMetadata) => {
      if (payloadHasMeaningfulStep1Content(serverPayload) || !writingMeta.hasContent) {
        setShowMissingStoryOffer(false);
        return;
      }
      const local = readStep1LocalBackup(userBackupKey);
      const hasLocal = !!(local && payloadHasMeaningfulStep1Content(local.payload));
      let hasArchives = false;
      try {
        const summaries = await listStep1ArchiveSummaries();
        hasArchives = summaries.length > 0;
      } catch {
        hasArchives = false;
      }
      setShowMissingStoryOffer(hasLocal || hasArchives);
    },
    [userBackupKey]
  );

  const persistAll = useCallback(
    async (
      nextPayload: Step1WritingInventoryPayload,
      nextMeta: Step1WritingMetadata,
      saveKind: Step1SaveKind = 'autosave',
      archiveReason?: string
    ) => {
      setSaveStatus('saving');
      try {
        const built = buildMetadataFromState(nextMeta, nextPayload);
        await saveStep1WritingPayload(nextPayload, {
          kind: saveKind,
          archiveReason,
          localBackupUserKey: userBackupKey,
        });
        await onSaveMetadata({ ...built, lastWorkedAt: new Date().toISOString() });
        setLocalMeta(built);
        setSaveStatus('saved');
      } catch (err) {
        setSaveStatus('error');
        if (err instanceof Error && err.message === 'STEP1_WRITING_WIPE_BLOCKED') {
          showConfirm({
            title: 'Your work was protected',
            message:
              'A save was stopped because it would have erased your story. Your last good copy is still safe.',
            confirmText: 'View earlier saves',
            cancelText: 'OK',
            variant: 'warning',
            onConfirm: () => void openRecoverPanel(),
          });
          try {
            const existing = await loadStep1WritingPayload();
            setPayload(existing);
            payloadLoadSucceededRef.current = true;
          } catch {
            payloadLoadSucceededRef.current = false;
            setLoadError('Could not reload your saved work. Try refreshing the page.');
          }
        } else if (err instanceof Error && err.message === 'STEP1_WRITING_LOAD_FAILED') {
          showAlert({
            title: 'Save failed',
            message: 'Could not verify your saved work before saving. Please try again.',
            variant: 'warning',
          });
        }
      }
    },
    [onSaveMetadata, showAlert, showConfirm, openRecoverPanel, userBackupKey]
  );

  const reloadPayload = useCallback(async () => {
    setIsPayloadLoading(true);
    setLoadError('');
    setShowMissingStoryOffer(false);
    try {
      const p = await loadStep1WritingPayload();
      applyPayloadFromServer(p);
      await checkForMissingStoryOffer(p, metadata);
    } catch {
      payloadLoadSucceededRef.current = false;
      const local = readStep1LocalBackup(userBackupKey);
      if (local && payloadHasMeaningfulStep1Content(local.payload)) {
        setLoadError(
          'We could not load your story from Recovery Commons. You may still have a save on this phone or computer.'
        );
      } else {
        setLoadError(
          'We could not load your saved work. Nothing was deleted on the server — try again or refresh the page.'
        );
      }
      setPayload(null);
    } finally {
      setIsPayloadLoading(false);
    }
  }, [applyPayloadFromServer, checkForMissingStoryOffer, metadata, userBackupKey]);

  const scheduleSave = useCallback(
    (nextPayload: Step1WritingInventoryPayload, nextMeta: Step1WritingMetadata) => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      autoSaveRef.current = setTimeout(() => {
        persistAll(nextPayload, nextMeta);
      }, 800);
    },
    [persistAll]
  );

  useEffect(() => {
    if (!isOpen) {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
        autoSaveRef.current = null;
      }
      if (payloadLoadSucceededRef.current && payloadRef.current) {
        void persistAll(payloadRef.current, metaRef.current, 'flush', 'Automatic snapshot on close');
      }
      payloadLoadSucceededRef.current = false;
      setLoadError('');
      setShowMissingStoryOffer(false);
      draftAnswersBaselineRef.current = null;
      return;
    }
    setLocalMeta(metadata);
    setShowPathPicker(!isStep1PathSelected(metadata));
    setSaveStatus('idle');
    setIsDraftEditorOpen(false);
    setIsStoryViewOpen(false);
    setIsDownloadPickerOpen(false);
    setIsClearAllOpen(false);
    setClearAllAcknowledged(false);
    setPayload(null);
    payloadLoadSucceededRef.current = false;
    void reloadPayload();
    // Load once when the modal opens — not on metadata autosave while it stays open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, []);

  const draftChecklistIssues = useMemo(() => {
    const text = (payload?.contentText || '').trim();
    if (!text) return [];
    return localDraftChecklist(text, payload?.wizardAnswers);
  }, [payload]);

  if (!isOpen) return null;

  if (isPayloadLoading || !payload) {
    return (
      <>
        <div className={`${styles.overlay} ${styles.overlayBusy}`} onClick={onClose}>
          <div
            className={`${styles.modal} ${styles.modalBusy}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="s1-writing-title"
            aria-busy={isPayloadLoading}
          >
            <div className={styles.header}>
              <div className={styles.headerInner}>
                <h2 id="s1-writing-title">First Step writing workspace</h2>
              </div>
              <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>
            <div className={`${styles.body} ${styles.bodyBusy}`}>
              {loadError ? (
                <div className={styles.loadErrorBlock}>
                  <p className={styles.loadErrorMessage}>{loadError}</p>
                  <div className={styles.loadErrorActions}>
                    <button type="button" className={styles.btnPrimary} onClick={() => void reloadPayload()}>
                      Try again
                    </button>
                    <button type="button" className={styles.btn} onClick={() => void openRecoverPanel()}>
                      View earlier saves
                    </button>
                  </div>
                </div>
              ) : (
                <p className={styles.loadingMessage}>Loading your saved work…</p>
              )}
            </div>
          </div>
        </div>
        {isRecoverOpen && (
          <Step1RecoverOverlay
            userBackupKey={userBackupKey}
            archiveSummaries={archiveSummaries}
            isArchivesLoading={isArchivesLoading}
            onClose={() => setIsRecoverOpen(false)}
            onRestoreArchive={restoreArchive}
            onRestoreLocal={restoreLocalBackup}
          />
        )}
      </>
    );
  }

  const currentPayload = payload;

  const contentText = currentPayload.contentText || '';
  const stats = estimateReadTime(contentText);
  const lengthLabel = contentText.trim() ? formatStoryLengthLabel(stats) : '';
  const wizardAnswersFingerprint = fingerprintWizardAnswers(currentPayload.wizardAnswers || {});
  const hasAssembledDraft = contentText.trim().length > 0;
  const draftAnswersBaseline =
    currentPayload.assembledAnswersFingerprint ?? draftAnswersBaselineRef.current;
  const draftNeedsReassemble =
    hasAssembledDraft &&
    draftAnswersBaseline != null &&
    wizardAnswersFingerprint !== draftAnswersBaseline;
  const draftIsCurrent = hasAssembledDraft && !draftNeedsReassemble;

  const stepIndexFor = (id: string): number => {
    if (!localMeta.mode) return 0;
    const idx = STEP1_METHOD_STEPS[localMeta.mode].findIndex((s) => s.id === id);
    return idx >= 0 ? idx : 0;
  };

  const renderMethodStepHeader = (stepIndex: number) => {
    if (!localMeta.mode) return null;
    const step = STEP1_METHOD_STEPS[localMeta.mode][stepIndex];
    if (!step) return null;
    return (
      <h3 className={styles.methodStepHeader}>
        <span className={styles.methodStepNumber}>{stepIndex + 1}.</span>{' '}
        <strong>{step.title}</strong>
        {' — '}
        <span className={styles.methodStepHeaderDetail}>{step.body}</span>
      </h3>
    );
  };

  const showsDraftChecklist = localMeta.mode === 'typed' || localMeta.mode === 'upload';

  const isLongDraft = stats.wordCount > LONG_DRAFT_WORD_THRESHOLD;

  const openDraftEditor = (options: { ariaLabel: string; placeholder?: string }) => {
    const baseline = contentText;
    setDraftEditBaseline(baseline);
    setDraftEditBuffer(baseline);
    setDraftEditorConfig({
      ariaLabel: options.ariaLabel,
      placeholder: options.placeholder,
      title: 'My First Step draft',
    });
    setIsDraftEditorOpen(true);
  };

  const closeDraftEditor = () => {
    setIsDraftEditorOpen(false);
    setDraftEditorConfig(null);
  };

  const requestCloseDraftEditor = () => {
    if (draftEditBuffer !== draftEditBaseline) {
      showConfirm({
        title: 'Discard changes?',
        message: 'You have unsaved edits to your draft. Discard them and close the editor?',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        variant: 'warning',
        onConfirm: closeDraftEditor,
      });
      return;
    }
    closeDraftEditor();
  };

  const saveDraftEditor = () => {
    const applyDraftSave = async () => {
      const nextPayload = { ...currentPayload, contentText: draftEditBuffer };
      await persistAll(nextPayload, localMeta, 'manual', 'Edited draft in draft editor');
      setPayload(nextPayload);
      closeDraftEditor();
    };
    const baselineLen = draftEditBaseline.trim().length;
    const bufferLen = draftEditBuffer.trim().length;
    if (baselineLen >= 50 && bufferLen === 0) {
      showConfirm({
        title: 'Save empty draft?',
        message:
          'This will clear your assembled story text. Your walkthrough answers are kept unless you change them separately. Your current work is saved first.',
        confirmText: 'Save empty draft',
        cancelText: 'Keep editing',
        variant: 'warning',
        onConfirm: () => void applyDraftSave(),
      });
      return;
    }
    void applyDraftSave();
  };

  const renderStoryTextarea = (options: {
    ariaLabel: string;
    placeholder?: string;
    editorRef?: React.RefObject<HTMLDivElement | null>;
  }) => {
    if (isLongDraft) {
      const aloud = formatAloudDurationLabel(stats.wordCount);
      return (
        <div className={styles.draftSummary} ref={options.editorRef}>
          <p>
            Your draft is ready — <strong>{stats.wordCount.toLocaleString()} words</strong>
            {aloud ? <> · {aloud}</> : null}. Use the draft editor to read or make changes.
          </p>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => openDraftEditor(options)}
          >
            Open draft editor
          </button>
        </div>
      );
    }

    return (
      <div className={styles.draftEditorBlock} ref={options.editorRef}>
        <textarea
          className={styles.textarea}
          value={contentText}
          onChange={(e) => updatePayload({ contentText: e.target.value })}
          placeholder={options.placeholder}
          aria-label={options.ariaLabel}
        />
      </div>
    );
  };

  const updatePayload = (patch: Partial<Step1WritingInventoryPayload>, autoSave = true) => {
    const next = { ...currentPayload, ...patch };
    setPayload(next);
    if (autoSave && payloadLoadSucceededRef.current) {
      scheduleSave(next, { ...localMeta, mode: localMeta.mode });
    }
  };

  const switchMode = async (mode: Step1WritingMode) => {
    const apply = async () => {
      const nextMeta: Step1WritingMetadata = { ...localMeta, mode, pathSelected: true };
      setLocalMeta(nextMeta);
      setShowPathPicker(false);
      await persistAll(currentPayload, nextMeta, 'manual', 'Changed writing method');
    };
    if (payloadHasMeaningfulStep1Content(currentPayload)) {
      showConfirm({
        title: 'Change writing method?',
        message:
          'Your walkthrough answers and draft are kept. Your current work is saved first. Continue?',
        confirmText: 'Change method',
        cancelText: 'Cancel',
        onConfirm: () => void apply(),
      });
      return;
    }
    await apply();
  };

  const returnToPathChoices = async () => {
    const apply = async () => {
      const nextMeta: Step1WritingMetadata = {
        ...localMeta,
        pathSelected: false,
        mode: undefined,
      };
      setLocalMeta(nextMeta);
      setShowPathPicker(true);
      await persistAll(currentPayload, nextMeta, 'manual', 'Returned to method chooser');
    };
    if (payloadHasMeaningfulStep1Content(currentPayload)) {
      showConfirm({
        title: 'Change method?',
        message:
          'Your work is kept. Your current work is saved first. Continue to the method chooser?',
        confirmText: 'Continue',
        cancelText: 'Cancel',
        onConfirm: () => void apply(),
      });
      return;
    }
    await apply();
  };

  const updatePresentationPlan = (patch: Partial<Step1PresentationPlan>) => {
    const plan = { ...(currentPayload.presentationPlan || {}), ...patch, updatedAt: new Date().toISOString() };
    updatePayload({ presentationPlan: plan });
  };

  const currentPrompt = STEP1_LIFE_STORY_PROMPTS[wizardPromptIndex];
  const isLastWizardPrompt = wizardPromptIndex >= STEP1_LIFE_STORY_PROMPTS.length - 1;
  const currentAnswer = currentPrompt ? currentPayload.wizardAnswers[currentPrompt.id] : undefined;

  const setWizardExample = (text: string) => {
    if (!currentPrompt) return;
    const prev = currentPayload.wizardAnswers[currentPrompt.id] || { examples: [''] };
    updatePayload({
      wizardAnswers: {
        ...currentPayload.wizardAnswers,
        [currentPrompt.id]: { ...prev, examples: [text], status: 'answered' },
      },
      wizardStepIndex: wizardPromptIndex,
      wizardChapterId: currentPrompt.chapterId,
    });
  };

  const skipWizardPrompt = () => {
    if (!currentPrompt) return;
    updatePayload({
      wizardAnswers: {
        ...currentPayload.wizardAnswers,
        [currentPrompt.id]: { examples: [], status: 'skipped' },
      },
      wizardStepIndex: wizardPromptIndex,
    });
    goWizardNext();
  };

  const goWizardNext = () => {
    if (wizardPromptIndex < STEP1_LIFE_STORY_PROMPTS.length - 1) {
      setWizardPromptIndex(wizardPromptIndex + 1);
    }
  };

  const goWizardPrev = () => {
    if (wizardPromptIndex > 0) setWizardPromptIndex(wizardPromptIndex - 1);
  };

  const handleAssembleDraft = async () => {
    const blocks = blocksForFlowWeave(currentPayload.wizardAnswers);
    if (blocks.length === 0) {
      showAlert({ message: 'Add some walkthrough answers first.', variant: 'info' });
      return;
    }
    const run = async () => {
      await runAssembleDraft(blocks);
    };
    if (hasAssembledDraft && draftNeedsReassemble) {
      showConfirm({
        title: 'Update assembled draft?',
        message:
          'This replaces your assembled draft with a fresh version from your walkthrough answers. Manual edits in the draft will be lost. Your current work is saved first.',
        confirmText: 'Update draft',
        cancelText: 'Cancel',
        onConfirm: () => void run(),
      });
      return;
    }
    if (contentText.trim().length > 50) {
      showConfirm({
        title: 'Assemble draft?',
        message:
          'This builds a new draft from your walkthrough answers and replaces your current story text. Your current work is saved first.',
        confirmText: 'Assemble',
        cancelText: 'Cancel',
        onConfirm: () => void run(),
      });
      return;
    }
    await run();
  };

  const runAssembleDraft = async (blocks: string[]) => {
    setIsAssembling(true);
    try {
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      const draft = localFlowWeave(blocks);
      const answersFingerprint = fingerprintWizardAnswers(currentPayload.wizardAnswers);
      const wasFirstAssembly = !hasAssembledDraft;
      const nextPayload: Step1WritingInventoryPayload = {
        ...currentPayload,
        contentText: draft,
        assembledAnswersFingerprint: answersFingerprint,
      };
      await persistAll(
        nextPayload,
        localMeta,
        'destructive',
        'Assembled draft from walkthrough answers'
      );
      setPayload(nextPayload);
      draftAnswersBaselineRef.current = answersFingerprint;
      if (wasFirstAssembly) {
        const assembledWordCount = draft.trim().split(/\s+/).filter(Boolean).length;
        const message =
          assembledWordCount > LONG_DRAFT_WORD_THRESHOLD
            ? 'Your answers were assembled into a draft. Open the draft editor when you are ready to review.'
            : 'Your answers were assembled into a draft below.';
        showAlert({ message, variant: 'info' });
      }
    } finally {
      setIsAssembling(false);
    }
  };

  const isBodyBusy = isAssembling;

  const renderMethodBanner = () => {
    if (!localMeta.mode) return null;
    return (
      <div className={styles.methodBanner}>
        <div className={styles.methodBannerRow}>
          <div className={styles.methodBannerLeft}>
            <span className={styles.methodBannerLabel}>
              Method: <strong>{methodTitle(localMeta.mode)}</strong>
            </span>
            <button type="button" className={styles.btnLink} onClick={returnToPathChoices}>
              Change method
            </button>
          </div>
          {lengthLabel && <span className={styles.methodLength}>{lengthLabel}</span>}
        </div>
      </div>
    );
  };

  const handleUpload = async (file: File) => {
    setUploadError('');
    try {
      const extracted = await extractStep1UploadFile(file);
      const run = async () => {
        const next = {
          ...currentPayload,
          contentText: extracted.text,
          originalFileName: extracted.fileName,
          originalMimeType: extracted.mimeType,
          originalFileBase64: extracted.base64,
        };
        setPayload(next);
        const nextMeta: Step1WritingMetadata = { ...localMeta, mode: 'upload', pathSelected: true };
        setLocalMeta(nextMeta);
        await persistAll(next, nextMeta, 'destructive', `Uploaded file: ${extracted.fileName}`);
      };
      if (payloadHasMeaningfulStep1Content(currentPayload)) {
        showConfirm({
          title: 'Replace with uploaded file?',
          message:
            'This replaces your current story text with the uploaded document. Your current work is saved first.',
          confirmText: 'Upload and replace',
          cancelText: 'Cancel',
          onConfirm: () => void run(),
        });
        return;
      }
      await run();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    }
  };

  const firstStepLastEditedAt =
    currentPayload.lastSavedAt || localMeta.lastWorkedAt || localMeta.lastUpdated;

  const handlePrint = () => {
    const html = generateFirstStepShareHtml(contentText, {
      lastEditedAt: firstStepLastEditedAt,
      includeShareNotice: false,
    });
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const handleDownload = async (format: FirstStepDownloadFormat) => {
    try {
      await downloadFirstStepStory(format, contentText, firstStepLastEditedAt);
      setIsDownloadPickerOpen(false);
    } catch {
      showAlert({ message: 'Download failed. Try again.', variant: 'warning' });
    }
  };

  const openClearAllDialog = () => {
    setClearAllAcknowledged(false);
    setIsClearAllOpen(true);
  };

  const handleClearAll = async () => {
    if (!clearAllAcknowledged || isClearingAll) return;
    setIsClearingAll(true);
    try {
      const empty = await clearStep1WritingAll(userBackupKey);
      const clearedMeta = createDefaultStep1WritingMetadata(
        localMeta.migrationSource || 'new_user_choose_path'
      );
      applyPayloadFromServer(empty);
      await onSaveMetadata(clearedMeta);
      setLocalMeta(clearedMeta);
      setShowPathPicker(true);
      setWizardPromptIndex(0);
      setIsClearAllOpen(false);
      setClearAllAcknowledged(false);
      setIsStoryViewOpen(false);
      setShowMissingStoryOffer(false);
      setSaveStatus('saved');
      showAlert({ message: 'Your First Step content was cleared.', variant: 'success' });
    } catch {
      showAlert({ message: 'Could not clear your content. Try again.', variant: 'warning' });
    } finally {
      setIsClearingAll(false);
    }
  };

  const hasClearableContent =
    payloadHasMeaningfulStep1Content(currentPayload) ||
    !!localMeta.externalNote?.trim() ||
    !!(localMeta.presentationPlan?.targetDate || localMeta.presentationPlan?.venue) ||
    !!currentPayload.originalFileName;

  const openStoryShare = () => {
    if (!contentText.trim()) {
      showAlert({ message: 'Add story text before sharing.', variant: 'info' });
      return;
    }
    showConfirm({
      title: 'Share First Step story',
      message:
        'This creates a link with your full story text. It expires in 3 days. Only share with people you trust (e.g. your sponsor).',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      onConfirm: () => setIsStoryShareOpen(true),
    });
  };

  const renderPresentationPlan = (stepIndex: number) => (
    <div ref={presentationSectionRef}>
      {renderMethodStepHeader(stepIndex)}
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="s1-target-date">Target date (optional)</label>
        <input
          id="s1-target-date"
          type="date"
          className={styles.input}
          value={currentPayload.presentationPlan?.targetDate || ''}
          onChange={(e) => updatePresentationPlan({ targetDate: e.target.value })}
        />
      </div>
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="s1-venue">Where you plan to share</label>
        <select
          id="s1-venue"
          className={styles.select}
          value={currentPayload.presentationPlan?.venue || ''}
          onChange={(e) =>
            updatePresentationPlan({
              venue: (e.target.value || undefined) as Step1PresentationPlan['venue'],
            })
          }
        >
          <option value="">— Select —</option>
          <option value="workshop">Workshop</option>
          <option value="home_meeting">Home meeting</option>
          <option value="sponsor_only">With sponsor only</option>
          <option value="other">Other</option>
        </select>
      </div>
      {currentPayload.presentationPlan?.venue === 'other' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="s1-venue-note">Venue note</label>
          <input
            id="s1-venue-note"
            className={styles.input}
            value={currentPayload.presentationPlan?.venueNote || ''}
            onChange={(e) => updatePresentationPlan({ venueNote: e.target.value })}
          />
        </div>
      )}
    </div>
  );

  const renderPathPicker = () => (
    <>
      <h3 className={styles.sectionTitle}>How would you like to write your First Step story?</h3>
      <p style={{ fontSize: '0.95rem', color: '#555', marginTop: 0 }}>
        Choose one approach to get started. You can change later — your saved work is kept in Recovery Commons.
      </p>
      <div className={styles.pathGrid}>
        {STEP1_PATH_OPTIONS.map((opt) => (
          <button
            key={opt.mode}
            type="button"
            className={`${styles.pathCard} ${
              isStep1PathSelected(localMeta) && localMeta.mode === opt.mode ? styles.pathCardSelected : ''
            }`}
            onClick={() => switchMode(opt.mode)}
          >
            <h3>{opt.title}</h3>
            <p>{opt.description}</p>
          </button>
        ))}
      </div>
      <div className={styles.encryptionNote}>{STEP1_ENCRYPTION_REMINDER}</div>
    </>
  );

  const renderExternal = () => (
    <>
      {renderMethodStepHeader(stepIndexFor('where-kept'))}
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="s1-external-note">Where I keep it (optional)</label>
        <input
          id="s1-external-note"
          className={styles.input}
          placeholder="e.g. journal at home, Google Doc, sponsor has a copy"
          value={localMeta.externalNote || ''}
          onChange={(e) => {
            const nextMeta = { ...localMeta, externalNote: e.target.value };
            setLocalMeta(nextMeta);
            scheduleSave(currentPayload, nextMeta);
          }}
        />
      </div>
      {renderPresentationPlan(stepIndexFor('presentation'))}
      {renderReviewSection()}
    </>
  );

  const renderLongDraftSection = (
    options: { ariaLabel: string; placeholder?: string; title?: string },
    editorRef?: React.RefObject<HTMLDivElement | null>
  ) => (
    <div className={styles.longDraftSection}>
      {options.title && <h3 className={styles.longDraftSectionTitle}>{options.title}</h3>}
      {renderStoryTextarea({ ...options, editorRef })}
    </div>
  );

  const renderTyped = () => (
    <>
      {renderMethodStepHeader(stepIndexFor('your-story'))}
      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          className={styles.btn}
          onClick={() => {
            const insert = async () => {
              const nextPayload = { ...currentPayload, contentText: STEP1_WRITING_TEMPLATE };
              setPayload(nextPayload);
              await persistAll(nextPayload, localMeta, 'destructive', 'Inserted story template');
            };
            if (contentText.trim()) {
              showConfirm({
                title: 'Insert template?',
                message:
                  'This replaces your current story text with the outline template. Your current work is saved first.',
                confirmText: 'Insert template',
                cancelText: 'Cancel',
                onConfirm: () => void insert(),
              });
              return;
            }
            void insert();
          }}
        >
          Insert template
        </button>
      </div>
      {!isLongDraft &&
        renderStoryTextarea({
          ariaLabel: 'First Step story',
          placeholder: 'Write your First Step story here…',
        })}
      {isLongDraft &&
        contentText.trim() &&
        renderLongDraftSection(
          {
            ariaLabel: 'First Step story',
            placeholder: 'Write your First Step story here…',
            title: 'Your story',
          },
          draftEditorRef
        )}
      {renderPathTail()}
    </>
  );

  const renderUpload = () => (
    <>
      {renderMethodStepHeader(stepIndexFor('upload'))}
      <div className={styles.uploadZone}>
        <p>Choose a file to upload</p>
        <input
          type="file"
          accept=".txt,.md,.docx,.pdf"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
          }}
        />
        {uploadError && <p style={{ color: '#b00020' }}>{uploadError}</p>}
        {currentPayload.originalFileName && (
          <p style={{ marginTop: 12 }}>
            File: <strong>{currentPayload.originalFileName}</strong>{' '}
            <button type="button" className={styles.btnLink} onClick={() => downloadOriginalFile(currentPayload)}>
              Download original
            </button>
          </p>
        )}
      </div>
      {contentText && !isLongDraft && (
        <>
          {renderMethodStepHeader(stepIndexFor('review-edit'))}
          {renderStoryTextarea({ ariaLabel: 'Extracted story text' })}
        </>
      )}
      {contentText &&
        isLongDraft && (
          <>
            {renderMethodStepHeader(stepIndexFor('review-edit'))}
            {renderLongDraftSection(
              { ariaLabel: 'Extracted story text', title: 'Extracted story' },
              draftEditorRef
            )}
          </>
        )}
      {renderPathTail()}
    </>
  );

  const renderWizardChapterTabs = () => (
    <div className={styles.chapterTabsBelow}>
      <span className={styles.chapterTabsLabel}>Jump to section:</span>
      <div className={styles.chapterTabs}>
        {STEP1_CHAPTERS.map((ch) => (
          <button
            key={ch.id}
            type="button"
            className={`${styles.chapterTab} ${currentPrompt?.chapterId === ch.id ? styles.chapterTabActive : ''}`}
            onClick={() => {
              const first = promptsForChapter(ch.id)[0];
              if (first) setWizardPromptIndex(globalPromptIndex(first.id));
            }}
          >
            {ch.title}
            {ch.optional ? ' (optional)' : ''}
          </button>
        ))}
      </div>
    </div>
  );

  const renderWizard = () => (
      <>
        {renderMethodStepHeader(stepIndexFor('walkthrough'))}

        {currentPrompt ? (
          <>
            <p className={styles.promptProgress}>
              Prompt{' '}
              <span className={styles.promptProgressNumber}>{wizardPromptIndex + 1}</span> of{' '}
              <span className={styles.promptProgressNumber}>
                {STEP1_LIFE_STORY_PROMPTS.length}
              </span>
            </p>
            <p className={styles.promptText}>{currentPrompt.text}</p>
            {currentPrompt.nudge && <p className={styles.promptHint}>{currentPrompt.nudge}</p>}
            <textarea
              className={styles.textarea}
              style={{ minHeight: 160 }}
              value={currentAnswer?.examples?.[0] || ''}
              onChange={(e) => setWizardExample(e.target.value)}
              placeholder={currentPrompt.placeholder}
            />
            <div className={styles.wizardNav}>
              <button type="button" className={styles.btn} onClick={goWizardPrev} disabled={wizardPromptIndex === 0}>
                Previous
              </button>
              <button type="button" className={styles.btnPrimary} onClick={goWizardNext} disabled={isLastWizardPrompt}>
                Next
              </button>
              <button type="button" className={styles.btn} onClick={skipWizardPrompt} disabled={isLastWizardPrompt}>
                Skip
              </button>
            </div>
          </>
        ) : null}

        {renderWizardChapterTabs()}

        {renderMethodStepHeader(stepIndexFor('assemble'))}

        {!hasAssembledDraft && (
          <div className={styles.wizardNav}>
            <button
              type="button"
              className={`${styles.btn} ${isAssembling ? styles.btnBusy : ''}`}
              onClick={handleAssembleDraft}
              disabled={isAssembling}
              aria-busy={isAssembling}
            >
              {isAssembling ? 'Assembling…' : 'Assemble my words'}
            </button>
          </div>
        )}

        {draftNeedsReassemble && (
          <div className={styles.assembleStatusBlock}>
            <p className={styles.assembleStaleNote}>
              You&apos;ve changed one or more walkthrough answers since this draft was assembled.
            </p>
            <button
              type="button"
              className={`${styles.btn} ${isAssembling ? styles.btnBusy : ''}`}
              onClick={handleAssembleDraft}
              disabled={isAssembling}
              aria-busy={isAssembling}
            >
              {isAssembling ? 'Updating draft…' : 'Update draft from my answers'}
            </button>
          </div>
        )}

        {draftIsCurrent && (
          <p className={styles.assembleCurrentNote}>
            Your assembled draft ({stats.wordCount.toLocaleString()} words) matches your current walkthrough
            answers.
          </p>
        )}

        {hasAssembledDraft &&
          (isLongDraft
            ? renderLongDraftSection(
                {
                  ariaLabel: 'Assembled First Step draft',
                  title: draftIsCurrent ? 'Your assembled draft' : 'Assembled draft',
                },
                draftEditorRef
              )
            : renderStoryTextarea({
                ariaLabel: 'Assembled First Step draft',
                editorRef: draftEditorRef,
              }))}

        {renderPathTail()}
      </>
  );

  const renderDraftChecklist = () => {
    if (draftChecklistIssues.length === 0) return null;
    return (
      <div className={styles.coachSection}>
        {renderMethodStepHeader(stepIndexFor('draft-checklist'))}
        <p className={styles.coachHint}>
          Optional reminders — pick what helps, ignore the rest. Your story is never changed for you.
          These are simple reminders, not an automated review of your writing.
        </p>
        <div className={styles.coachFeedback} role="region" aria-label="Draft checklist">
          <ul className={styles.coachList}>
            {draftChecklistIssues.map((issue) => {
              const isStrength = issue.severity === 'info';
              const hasTry = !isStrength && issue.recommendation.trim().length > 0;
              return (
                <li
                  key={issue.id}
                  className={`${styles.coachListItem} ${isStrength ? styles.coachListItemStrength : ''}`}
                >
                  {isStrength ? (
                    <p className={styles.coachStrengthLine}>{issue.feedback}</p>
                  ) : (
                    <>
                      <p className={styles.coachFeedbackLine}>{issue.feedback}</p>
                      {hasTry && (
                        <p className={styles.coachRecommendationLine}>
                          <span className={styles.coachFeedbackLabel}>Consider:</span>{' '}
                          {issue.recommendation}
                        </p>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  const renderReviewSection = () => {
    if (!localMeta.mode) return null;
    return (
      <div className={styles.reviewSection} ref={reviewSectionRef}>
        {renderMethodStepHeader(stepIndexFor('review'))}
        {contentText.trim() && (
          <div className={styles.reviewActions}>
            <button type="button" className={styles.btn} onClick={() => setIsStoryViewOpen(true)}>
              View
            </button>
            <button type="button" className={styles.btn} onClick={() => setIsDownloadPickerOpen(true)}>
              Download
            </button>
            <button type="button" className={styles.btn} onClick={handlePrint}>
              Print
            </button>
            <button type="button" className={styles.btnPrimary} onClick={openStoryShare}>
              Share First Step story
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPathTail = () => (
    <>
      {showsDraftChecklist && renderDraftChecklist()}
      {renderPresentationPlan(stepIndexFor('presentation'))}
      {renderReviewSection()}
    </>
  );

  const renderMainView = () => {
    if (showPathPicker || !isStep1PathSelected(localMeta)) return renderPathPicker();
    switch (localMeta.mode) {
      case 'external':
        return renderExternal();
      case 'upload':
        return renderUpload();
      case 'typed':
        return renderTyped();
      case 'wizard':
        return renderWizard();
      default:
        return renderPathPicker();
    }
  };

  const renderStoryViewOverlay = () => {
    if (!isStoryViewOpen) return null;
    const html = generateFirstStepShareHtml(contentText, {
      lastEditedAt: firstStepLastEditedAt,
      includeShareNotice: false,
    });
    return (
      <div
        className={styles.storyViewOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s1-story-view-title"
      >
        <div className={styles.storyViewToolbar}>
          <h2 id="s1-story-view-title" className={styles.storyViewToolbarTitle}>
            My First Step
          </h2>
          <div className={styles.storyViewToolbarActions}>
            <button type="button" className={styles.btn} onClick={() => setIsDownloadPickerOpen(true)}>
              Download
            </button>
            <button type="button" className={styles.btn} onClick={handlePrint}>
              Print
            </button>
            <button type="button" className={styles.btnPrimary} onClick={() => setIsStoryViewOpen(false)}>
              Close
            </button>
          </div>
        </div>
        <iframe className={styles.storyViewFrame} srcDoc={html} title="My First Step" />
      </div>
    );
  };

  const renderDownloadPicker = () => {
    if (!isDownloadPickerOpen) return null;
    return (
      <div className={styles.subModalOverlay} onClick={() => setIsDownloadPickerOpen(false)}>
        <div
          className={styles.subModal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="s1-download-title"
          aria-modal="true"
        >
          <div className={styles.subModalHeader}>
            <h2 id="s1-download-title">Download My First Step</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setIsDownloadPickerOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className={styles.subModalBody}>
            <p className={styles.subModalIntro}>Choose a format to save on your device.</p>
            <div className={styles.downloadFormatActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => void handleDownload('pdf')}
              >
                PDF (.pdf)
              </button>
              <button type="button" className={styles.btn} onClick={() => void handleDownload('docx')}>
                Word document (.docx)
              </button>
              <button type="button" className={styles.btn} onClick={() => void handleDownload('txt')}>
                Plain text (.txt)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderClearAllDialog = () => {
    if (!isClearAllOpen) return null;
    return (
      <div className={styles.subModalOverlay} onClick={() => !isClearingAll && setIsClearAllOpen(false)}>
        <div
          className={styles.subModal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="s1-clear-title"
          aria-modal="true"
        >
          <div className={styles.subModalHeader}>
            <h2 id="s1-clear-title">Clear all First Step content</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => !isClearingAll && setIsClearAllOpen(false)}
              aria-label="Close"
              disabled={isClearingAll}
            >
              ×
            </button>
          </div>
          <div className={styles.subModalBody}>
            <p className={styles.subModalIntro}>
              This permanently removes your First Step story, walkthrough answers, uploaded document,
              and presentation plan from Recovery Commons.
            </p>
            <p className={styles.subModalIntro}>
              Save a copy somewhere you control before continuing — for example on your computer, in
              email, or with your sponsor.
            </p>
            {contentText.trim() && (
              <div className={styles.clearAllExportActions}>
                <button type="button" className={styles.btn} onClick={() => setIsStoryViewOpen(true)}>
                  View
                </button>
                <button type="button" className={styles.btn} onClick={() => setIsDownloadPickerOpen(true)}>
                  Download
                </button>
                <button type="button" className={styles.btn} onClick={handlePrint}>
                  Print
                </button>
              </div>
            )}
            <label className={styles.clearAllCheckbox}>
              <input
                type="checkbox"
                checked={clearAllAcknowledged}
                onChange={(e) => setClearAllAcknowledged(e.target.checked)}
                disabled={isClearingAll}
              />
              <span>I have saved a copy outside Recovery Commons (or I don&apos;t need one).</span>
            </label>
          </div>
          <div className={styles.subModalFooter}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => setIsClearAllOpen(false)}
              disabled={isClearingAll}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.btnDanger}
              onClick={() => void handleClearAll()}
              disabled={!clearAllAcknowledged || isClearingAll}
            >
              {isClearingAll ? 'Clearing…' : 'Clear all First Step content'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRecoverPanel = () => {
    if (!isRecoverOpen) return null;
    return (
      <Step1RecoverOverlay
        userBackupKey={userBackupKey}
        archiveSummaries={archiveSummaries}
        isArchivesLoading={isArchivesLoading}
        onClose={() => setIsRecoverOpen(false)}
        onRestoreArchive={restoreArchive}
        onRestoreLocal={restoreLocalBackup}
      />
    );
  };

  const renderDraftEditorSubModal = () => {
    if (!isDraftEditorOpen || !draftEditorConfig) return null;
    const editStats = estimateReadTime(draftEditBuffer);
    const editLengthLabel = formatStoryLengthLabel(editStats);
    const isDirty = draftEditBuffer !== draftEditBaseline;

    return (
      <div className={styles.draftEditorOverlay} onClick={requestCloseDraftEditor}>
        <div
          className={styles.draftEditorModal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="s1-draft-editor-title"
          aria-modal="true"
        >
          <div className={styles.draftEditorHeader}>
            <div className={styles.draftEditorHeaderInner}>
              <h2 id="s1-draft-editor-title">{draftEditorConfig.title}</h2>
              {editLengthLabel && <p className={styles.draftEditorHeaderMeta}>{editLengthLabel}</p>}
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={requestCloseDraftEditor}
              aria-label="Close draft editor"
            >
              ×
            </button>
          </div>
          <div className={styles.draftEditorBody}>
            <textarea
              className={styles.draftEditorTextarea}
              value={draftEditBuffer}
              onChange={(e) => setDraftEditBuffer(e.target.value)}
              placeholder={draftEditorConfig.placeholder}
              aria-label={draftEditorConfig.ariaLabel}
              autoFocus
            />
          </div>
          <div className={styles.draftEditorFooter}>
            <span className={styles.draftEditorDirtyNote}>
              {isDirty ? 'Unsaved changes' : '\u00a0'}
            </span>
            <div className={styles.draftEditorFooterActions}>
              <button type="button" className={styles.btn} onClick={requestCloseDraftEditor}>
                Cancel
              </button>
              <button type="button" className={styles.btnPrimary} onClick={saveDraftEditor}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="s1-writing-title">
          <div className={styles.header}>
            <div className={styles.headerInner}>
              <h2 id="s1-writing-title">First Step writing workspace</h2>
              {isStep1PathSelected(localMeta) && !showPathPicker && renderMethodBanner()}
            </div>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div ref={bodyRef} className={`${styles.body} ${isBodyBusy ? styles.bodyBusy : ''}`}>
            {showMissingStoryOffer && (
              <div className={styles.missingStoryOffer}>
                <p>
                  Your story looks empty here, but we found earlier saves. Would you like to look at
                  them?
                </p>
                <div className={styles.missingStoryOfferActions}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={() => void openRecoverPanel()}
                  >
                    View earlier saves
                  </button>
                  <button
                    type="button"
                    className={styles.btnLink}
                    onClick={() => setShowMissingStoryOffer(false)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            {renderMainView()}
          </div>

          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <span className={saveStatus === 'error' ? styles.saveStatusError : styles.saveStatus}>
                {saveStatus === 'saving' && 'Saving…'}
                {saveStatus === 'saved' && 'Saved'}
                {saveStatus === 'error' && 'Save failed — retry by editing'}
              </span>
              {saveStatus === 'error' && (
                <button type="button" className={styles.btnLink} onClick={() => void openRecoverPanel()}>
                  Something missing? View earlier saves
                </button>
              )}
            </div>
            {hasClearableContent && (
              <div className={styles.footerCenter}>
                <button type="button" className={styles.btnLinkMuted} onClick={openClearAllDialog}>
                  Clear First Step content (requires confirmation)
                </button>
              </div>
            )}
            <div className={styles.footerRight}>
              <button type="button" className={styles.btn} onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {renderRecoverPanel()}

      {renderDownloadPicker()}

      {renderClearAllDialog()}

      {renderStoryViewOverlay()}

      {renderDraftEditorSubModal()}

      <StepShareDialog
        isOpen={isStoryShareOpen}
        onClose={() => setIsStoryShareOpen(false)}
        user={user}
        stepTitle="Step One"
        shareName="My First Step"
        documentType="step-1-first-step"
        generateHtml={() =>
          generateFirstStepShareHtml(contentText, {
            lastEditedAt: firstStepLastEditedAt,
            includeShareNotice: true,
          })
        }
        canGenerate={() => contentText.trim().length > 0}
        emptyMessage="Add story text before sharing."
      />
    </>
  );
};

export default Step1WritingModal;
